import { useState, useEffect } from "react";
import axios from "axios";
import HeaderBar from "./components/headerBar";
import { 
  Search, 
  LifeBuoy, 
  CreditCard, 
  BookOpen, 
  HelpCircle, 
  ChevronRight, 
  ChevronDown,
  Send,
  CheckCircle2,
  Clock,
  MessageSquare,
  X,
  MessageCircle
} from "lucide-react";
import Button from "../../components/common/Button";
import { motion, AnimatePresence } from "framer-motion";

const CategoryCard = ({ icon: Icon, title, description }) => (
  <button className="cht-group cht-p-8 cht-bg-gray-50 cht-rounded-[32px] cht-border cht-border-transparent hover:cht-border-[#3e57da]/30 hover:cht-bg-white hover:cht-shadow-[0_20px_40px_rgba(0,0,0,0.04)] cht-transition-all cht-duration-500 cht-text-left">
    <div className="cht-w-14 cht-h-14 cht-bg-white cht-rounded-[20px] cht-flex cht-items-center cht-justify-center cht-shadow-sm cht-mb-6 group-hover:cht-bg-[#3e57da] group-hover:cht-text-white transition-all">
      <Icon size={28} />
    </div>
    <h4 className="cht-text-xl cht-font-black cht-text-black">{title}</h4>
    <p className="cht-text-sm cht-text-gray-500 cht-mt-3 cht-font-medium leading-relaxed">{description}</p>
    <div className="cht-mt-6 cht-flex cht-items-center cht-gap-2 cht-text-[#3e57da] cht-text-[10px] cht-font-black cht-uppercase cht-tracking-wider cht-opacity-0 group-hover:cht-opacity-100 transition-all">
      Get Help <ChevronRight size={14} />
    </div>
  </button>
);

const FAQ_ITEMS = [
  {
    question: "How do I download my certificate?",
    answer: "Once you complete all course modules and assignments with a passing grade, head to your student dashboard. A 'Download Certificate' option will appear on your course card, allowing you to save a verified PDF copy."
  },
  {
    question: "Can I switch from Online to Onsite?",
    answer: "Yes, you can transition between learning formats. Please contact support or visit the administration office. A format switch requires auditing seat availability and settling any tuition difference."
  },
  {
    question: "Where can I find the course source code?",
    answer: "All project source code, Figma design files, and digital resources are hosted in the 'Resources' tab of individual lesson modules. You can download them directly from the web interface."
  },
  {
    question: "How to request a refund?",
    answer: "Refund requests are valid within 7 days of enrollment, provided you have completed less than 15% of the course content. To request a refund, open a support ticket under the Billing & Payments category."
  }
];

const MOCK_TICKETS = [
  {
    id: 1,
    ticket_number: "TK-3081",
    topic: "Billing Question",
    subject: "Discrepancy in receipt description",
    description: "My PDF receipt states that I signed up for the online course, but I requested the onsite track. Please verify and update my profile.",
    status: "Resolved",
    created_at: "2026-05-25T10:14:00Z",
    last_updated: "2026-05-26T11:45:00Z",
    messages: [
      {
        id: 1,
        sender: "student",
        sender_name: "Student",
        content: "My PDF receipt states that I signed up for the online course, but I requested the onsite track. Please verify and update my profile.",
        timestamp: "2026-05-25T10:14:00Z"
      },
      {
        id: 2,
        sender: "agent",
        sender_name: "Chioma (Support)",
        content: "Hello! We have reviewed your invoice. Your physical seat in the Lagos studio has been reserved, and we have updated your receipt accordingly. You can download the new version from your Payments tab.",
        timestamp: "2026-05-26T09:20:00Z"
      },
      {
        id: 3,
        sender: "student",
        sender_name: "Student",
        content: "Perfect, thank you! I can see the onsite designation now.",
        timestamp: "2026-05-26T11:45:00Z"
      }
    ]
  },
  {
    id: 2,
    ticket_number: "TK-4029",
    topic: "Technical Issue",
    subject: "Buffering issues on Module 3, Lesson 4 video",
    description: "The video player keeps buffering around the 4:12 mark. Other videos load fine. Checked my network speed and it is 100Mbps.",
    status: "In Progress",
    created_at: "2026-05-28T08:30:00Z",
    last_updated: "2026-05-28T09:45:00Z",
    messages: [
      {
        id: 4,
        sender: "student",
        sender_name: "Student",
        content: "The video player keeps buffering around the 4:12 mark. Other videos load fine. Checked my network speed and it is 100Mbps.",
        timestamp: "2026-05-28T08:30:00Z"
      },
      {
        id: 5,
        sender: "agent",
        sender_name: "Tunde (Engineering)",
        content: "Hi there! Thanks for reporting this. We've inspected the server CDN node for that specific video chunk. We're currently clearing its cache. Could you try reloading in private browsing mode in about 15 minutes?",
        timestamp: "2026-05-28T09:45:00Z"
      }
    ]
  }
];

const formatMessageTime = (isoString) => {
  if (!isoString) return "";
  try {
    const d = new Date(isoString);
    if (isNaN(d.getTime())) return isoString;
    return d.toLocaleDateString() + " " + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch (e) {
    return isoString;
  }
};

const TicketStatusBadge = ({ status }) => {
  const styles = {
    "Open": "cht-bg-amber-50 cht-text-amber-600 cht-border-amber-100",
    "In Progress": "cht-bg-blue-50 cht-text-[#3e57da] cht-border-blue-100",
    "Resolved": "cht-bg-green-50 cht-text-green-600 cht-border-green-100",
  };
  return (
    <span className={`cht-text-[10px] cht-font-black cht-uppercase cht-tracking-widest cht-px-3 cht-py-1.5 cht-rounded-full cht-border ${styles[status]}`}>
      {status}
    </span>
  );
};

const FaqAccordionItem = ({ question, answer, isOpen, onToggle }) => {
  return (
    <div 
      className={`cht-border cht-rounded-[18px] cht-overflow-hidden cht-transition-all cht-duration-300 ${
        isOpen 
          ? "cht-bg-[#f2f4fd]/50 cht-border-[#3e57da]/20 cht-shadow-sm" 
          : "cht-bg-gray-50/50 cht-border-transparent hover:cht-border-[#3e57da]/10 hover:cht-bg-gray-50"
      }`}
    >
      <button
        type="button"
        onClick={onToggle}
        className="cht-w-full cht-flex cht-items-center cht-justify-between cht-p-5 cht-text-left"
      >
        <span className={`cht-text-sm cht-font-bold cht-transition-colors ${
          isOpen ? "cht-text-[#3e57da]" : "cht-text-black"
        }`}>
          {question}
        </span>
        <motion.div
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
          className={`cht-flex-shrink-0 cht-w-7 cht-h-7 cht-rounded-full cht-flex cht-items-center cht-justify-center cht-transition-all ${
            isOpen ? "cht-bg-[#3e57da] cht-text-white" : "cht-bg-white cht-text-gray-400 cht-shadow-sm"
          }`}
        >
          <ChevronDown size={14} />
        </motion.div>
      </button>
      
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            className="cht-overflow-hidden"
          >
            <div className="cht-px-5 cht-pb-5 cht-text-xs cht-text-gray-500 cht-font-medium cht-leading-relaxed">
              {answer}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const SupportPage = () => {
  const [submitted, setSubmitted] = useState(false);
  const [activeFaq, setActiveFaq] = useState(null);
  
  // Ticketing and history states loaded from backend
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);

  const [activeTab, setActiveTab] = useState("submit"); // 'submit' | 'history'
  const [topic, setTopic] = useState("Technical Issue");
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  // Load tickets on mount (Backend API with Local fallback)
  useEffect(() => {
    const loadTickets = async () => {
      const token = localStorage.getItem("access");
      if (!token) {
        // Fallback to local storage if user is not authenticated yet in the dashboard
        const saved = localStorage.getItem("support_tickets");
        if (saved) {
          try {
            setTickets(JSON.parse(saved));
          } catch (e) {
            console.error(e);
          }
        } else {
          localStorage.setItem("support_tickets", JSON.stringify(MOCK_TICKETS));
          setTickets(MOCK_TICKETS);
        }
        setLoading(false);
        return;
      }

      const config = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `JWT ${token}`,
          Accept: "application/json",
        },
      };

      try {
        setLoading(true);
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/support/tickets/`,
          config
        );
        setTickets(response.data);
      } catch (error) {
        console.error("Failed to load tickets from backend, falling back to local storage:", error);
        const saved = localStorage.getItem("support_tickets");
        if (saved) {
          try {
            setTickets(JSON.parse(saved));
          } catch (e) {
            console.error(e);
          }
        } else {
          setTickets(MOCK_TICKETS);
        }
      } finally {
        setLoading(false);
      }
    };

    loadTickets();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!subject.trim() || !description.trim()) return;

    const token = localStorage.getItem("access");
    if (!token) {
      // Local fallback submission
      const newTicket = {
        id: Date.now(),
        ticket_number: `TK-${Math.floor(1000 + Math.random() * 9000)}`,
        topic,
        subject,
        description,
        status: "Open",
        created_at: new Date().toISOString(),
        last_updated: new Date().toISOString(),
        messages: [
          {
            id: Date.now(),
            sender: "student",
            sender_name: "Student",
            content: description,
            timestamp: new Date().toISOString()
          }
        ]
      };
      const updated = [newTicket, ...tickets];
      setTickets(updated);
      localStorage.setItem("support_tickets", JSON.stringify(updated));

      setSubject("");
      setDescription("");
      setSubmitted(true);

      setTimeout(() => {
        setSubmitted(false);
        setActiveTab("history");
      }, 2000);
      return;
    }

    const config = {
      headers: {
        "Content-Type": "application/json",
        Authorization: `JWT ${token}`,
        Accept: "application/json",
      },
    };

    const payload = { topic, subject, description };

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/support/tickets/`,
        payload,
        config
      );

      setTickets([response.data, ...tickets]);
      setSubject("");
      setDescription("");
      setSubmitted(true);

      setTimeout(() => {
        setSubmitted(false);
        setActiveTab("history");
      }, 2000);
    } catch (error) {
      console.error("Failed to open ticket on backend:", error);
    }
  };

  const handleSendReply = async (ticketId) => {
    if (!replyText.trim()) return;

    const token = localStorage.getItem("access");
    if (!token) {
      // Local fallback reply
      const updatedTickets = tickets.map((t) => {
        if (t.id === ticketId || t.ticket_number === ticketId) {
          const newMsg = {
            id: Date.now(),
            sender: "student",
            sender_name: "Student",
            content: replyText,
            timestamp: new Date().toISOString()
          };

          return {
            ...t,
            messages: [...t.messages, newMsg],
            status: t.status === "Resolved" ? "Open" : t.status,
            last_updated: new Date().toISOString()
          };
        }
        return t;
      });

      setTickets(updatedTickets);
      localStorage.setItem("support_tickets", JSON.stringify(updatedTickets));
      const currentTicket = updatedTickets.find(t => t.id === ticketId || t.ticket_number === ticketId);
      setSelectedTicket(currentTicket);
      setReplyText("");
      return;
    }

    const config = {
      headers: {
        "Content-Type": "application/json",
        Authorization: `JWT ${token}`,
        Accept: "application/json",
      },
    };

    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/support/tickets/${ticketId}/reply/`,
        { content: replyText },
        config
      );

      // Reload tickets to fetch updated messages thread
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/support/tickets/`,
        config
      );
      setTickets(response.data);
      const activeTicket = response.data.find(t => t.id === ticketId);
      setSelectedTicket(activeTicket);
      setReplyText("");
    } catch (error) {
      console.error("Failed to send reply to backend:", error);
    }
  };

  const filteredTickets = tickets.filter((t) => 
    t.subject.toLowerCase().includes(searchQuery.toLowerCase()) || 
    t.topic.toLowerCase().includes(searchQuery.toLowerCase()) ||
    t.ticket_number.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <section className="cht-animate-in cht-fade-in cht-duration-700">
      <HeaderBar pageHeader="Help & Support" />
      
      <main className="cht-container cht-p-0 md:cht-p-6 cht-space-y-16 pb-12">
        
        {/* Genius Bar Hero */}
        <div className="cht-max-w-3xl cht-mx-auto cht-text-center cht-space-y-8">
          <h1 className="cht-text-5xl cht-font-black cht-text-black cht-tracking-tight">Genius Bar Support</h1>
          <p className="cht-text-lg cht-text-gray-500 cht-font-medium">Search our knowledge base or track your open tickets.</p>
          
          <div className="cht-relative cht-group">
            <Search className="cht-absolute cht-left-6 cht-top-1/2 -cht-translate-y-1/2 cht-text-gray-400 group-focus-within:cht-text-[#3e57da] transition-colors" size={24} />
            <input 
              type="text" 
              placeholder="How can we help you today?" 
              className="cht-w-full cht-bg-white cht-border-none cht-rounded-[32px] cht-py-6 cht-pl-16 cht-pr-8 cht-text-lg cht-font-bold cht-shadow-[0_10px_60px_rgba(0,0,0,0.05)] cht-outline-none focus:cht-ring-4 focus:cht-ring-[#3e57da]/10 transition-all"
            />
          </div>
        </div>

        {/* Category Grid */}
        <div className="cht-grid cht-grid-cols-1 md:cht-grid-cols-2 lg:cht-grid-cols-4 cht-gap-8">
          <CategoryCard 
            icon={LifeBuoy} 
            title="Technical Support" 
            description="Issues with video playback, code editor, or site performance?" 
          />
          <CategoryCard 
            icon={CreditCard} 
            title="Billing & Payments" 
            description="Questions about receipts, refunds, or payment methods." 
          />
          <CategoryCard 
            icon={BookOpen} 
            title="Course Content" 
            description="Need clarification on a lesson or specific module content?" 
          />
          <CategoryCard 
            icon={HelpCircle} 
            title="General Inquiry" 
            description="Anything else on your mind? We'd love to hear from you." 
          />
        </div>

        {/* Double Tab Switcher */}
        <div className="cht-flex cht-justify-center cht-gap-3 cht-mb-12">
          <button
            onClick={() => setActiveTab("submit")}
            className={`cht-px-6 cht-py-3.5 cht-rounded-full cht-text-xs cht-font-black cht-uppercase cht-tracking-widest cht-transition-all ${
              activeTab === "submit"
                ? "cht-bg-[#3e57da] cht-text-white hover:cht-text-white cht-shadow-md"
                : "cht-bg-white cht-text-gray-400 hover:cht-bg-gray-100 hover:cht-text-gray-600"
            }`}
          >
            Submit a Ticket
          </button>
          <button
            onClick={() => setActiveTab("history")}
            className={`cht-px-6 cht-py-3.5 cht-rounded-full cht-text-xs cht-font-black cht-uppercase cht-tracking-widest cht-transition-all ${
              activeTab === "history"
                ? "cht-bg-[#3e57da] cht-text-white hover:cht-text-white cht-shadow-md"
                : "cht-bg-white cht-text-gray-400 hover:cht-bg-gray-100 hover:cht-text-gray-600"
            }`}
          >
            My Tickets ({tickets.length})
          </button>
        </div>

        {/* Tab Panels */}
        {activeTab === "submit" ? (
          <div className="cht-grid cht-grid-cols-1 lg:cht-grid-cols-12 cht-gap-12 cht-bg-white cht-p-12 cht-rounded-[48px] cht-shadow-[0_20px_60px_rgba(0,0,0,0.02)] cht-border cht-border-gray-50">
            
            {/* FAQ Sidebar */}
            <div className="lg:cht-col-span-4 cht-space-y-8">
              <h3 className="cht-text-2xl cht-font-black cht-text-black">Quick FAQ</h3>
              <div className="cht-space-y-4">
                {FAQ_ITEMS.map((faq, i) => (
                  <FaqAccordionItem
                    key={i}
                    question={faq.question}
                    answer={faq.answer}
                    isOpen={activeFaq === i}
                    onToggle={() => setActiveFaq(activeFaq === i ? null : i)}
                  />
                ))}
              </div>
              <div className="cht-p-6 cht-bg-[#3e57da]/5 cht-rounded-[24px] cht-space-y-4">
                <div className="cht-flex cht-items-center cht-gap-3 cht-text-[#3e57da]">
                  <Clock size={20} />
                  <span className="cht-text-xs cht-font-black cht-uppercase cht-tracking-widest">Avg. Response Time</span>
                </div>
                <p className="cht-text-2xl cht-font-black cht-text-black">2.4 Hours</p>
                <p className="cht-text-xs cht-text-gray-400 cht-font-bold">Our team is active Monday - Friday.</p>
              </div>
            </div>

            {/* Ticket Form */}
            <div className="lg:cht-col-span-8">
              {submitted ? (
                <div className="cht-h-full cht-flex cht-flex-col cht-items-center cht-justify-center cht-text-center cht-space-y-6 cht-animate-in cht-zoom-in-95 cht-duration-500">
                  <div className="cht-w-24 cht-h-24 cht-bg-green-100 cht-text-green-600 cht-rounded-full cht-flex cht-items-center cht-justify-center cht-shadow-xl shadow-green-100/50">
                    <CheckCircle2 size={48} />
                  </div>
                  <div className="cht-space-y-2">
                    <h3 className="cht-text-3xl cht-font-black cht-text-black">Ticket Opened</h3>
                    <p className="cht-text-gray-500 cht-font-medium max-w-sm mx-auto">We have logged your ticket. You can track its status under the "My Tickets" tab now.</p>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="cht-space-y-8">
                  <h3 className="cht-text-2xl cht-font-black cht-text-black">Open a Support Ticket</h3>
                  <div className="cht-grid cht-grid-cols-1 md:cht-grid-cols-2 cht-gap-6">
                    <div className="cht-space-y-2">
                      <label className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest cht-ml-4">Topic</label>
                      <select 
                        value={topic}
                        onChange={(e) => setTopic(e.target.value)}
                        className="cht-w-full cht-bg-gray-50 cht-border-none cht-rounded-[20px] cht-py-4 cht-px-6 cht-text-sm cht-font-bold cht-outline-none focus:cht-ring-2 focus:cht-ring-[#3e57da]/10 transition-all"
                      >
                        <option>Technical Issue</option>
                        <option>Billing Question</option>
                        <option>Course Content Help</option>
                        <option>Other</option>
                      </select>
                    </div>
                    <div className="cht-space-y-2">
                      <label className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest cht-ml-4">Subject</label>
                      <input 
                        type="text" 
                        value={subject}
                        onChange={(e) => setSubject(e.target.value)}
                        placeholder="Summary of issue" 
                        className="cht-w-full cht-bg-gray-50 cht-border-none cht-rounded-[20px] cht-py-4 cht-px-6 cht-text-sm cht-font-bold cht-outline-none focus:cht-ring-2 focus:cht-ring-[#3e57da]/10 transition-all" 
                        required
                      />
                    </div>
                  </div>
                  <div className="cht-space-y-2">
                    <label className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest cht-ml-4">Description</label>
                    <textarea 
                      rows={5} 
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="Tell us more about what you need help with..." 
                      className="cht-w-full cht-bg-gray-50 cht-border-none cht-rounded-[24px] cht-py-4 cht-px-6 cht-text-sm cht-font-bold cht-outline-none focus:cht-ring-2 focus:cht-ring-[#3e57da]/10 transition-all resize-none"
                      required
                    ></textarea>
                  </div>
                  <div className="cht-flex cht-items-center cht-justify-between">
                    <div className="cht-flex cht-items-center cht-gap-2 cht-text-[10px] cht-font-bold cht-text-gray-400">
                      <MessageSquare size={14} /> Live chat is available 9am-5pm WAT
                    </div>
                    <Button 
                      type="submit" 
                      variant="secondary"
                      className="cht-bg-black cht-border-none hover:cht-bg-gray-800"
                      rightIcon={<Send size={16} />}
                    >
                      Send Ticket
                    </Button>
                  </div>
                </form>
              )}
            </div>
          </div>
        ) : (
          <div className="cht-bg-white cht-p-8 md:cht-p-12 cht-rounded-[48px] cht-shadow-[0_20px_60px_rgba(0,0,0,0.02)] cht-border cht-border-gray-50 cht-space-y-8 cht-animate-in cht-fade-in cht-duration-500">
            <div className="cht-flex cht-flex-col md:cht-flex-row md:cht-items-center cht-justify-between cht-gap-4">
              <div className="cht-space-y-1">
                <h3 className="cht-text-2xl cht-font-black cht-text-black">Ticket History</h3>
                <p className="cht-text-sm cht-text-gray-400 cht-font-medium">Track your technical, content, or billing inquiries.</p>
              </div>
              
              {/* Search Filter */}
              <div className="cht-relative md:cht-w-72">
                <Search className="cht-absolute cht-left-4 cht-top-1/2 -cht-translate-y-1/2 cht-text-gray-400" size={16} />
                <input
                  type="text"
                  placeholder="Filter tickets..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="cht-w-full cht-bg-gray-50 cht-border-none cht-rounded-full cht-py-2.5 cht-pl-11 cht-pr-4 cht-text-xs cht-font-bold cht-outline-none focus:cht-ring-2 focus:cht-ring-[#3e57da]/10 transition-all"
                />
              </div>
            </div>

            {loading ? (
              <div className="cht-text-center cht-py-12 cht-text-gray-400 cht-font-bold">
                Loading tickets...
              </div>
            ) : filteredTickets.length === 0 ? (
              <div className="cht-text-center cht-py-16 cht-space-y-4">
                <div className="cht-w-16 cht-h-16 cht-bg-gray-50 cht-text-gray-300 cht-rounded-full cht-flex cht-items-center cht-justify-center cht-mx-auto">
                  <MessageCircle size={28} />
                </div>
                <p className="cht-text-sm cht-text-gray-400 cht-font-bold">No tickets found.</p>
              </div>
            ) : (
              <div className="cht-overflow-x-auto">
                <table className="cht-w-full cht-text-left cht-border-collapse">
                  <thead>
                    <tr className="cht-border-b cht-border-gray-100" style={{ height: 48 }}>
                      <th className="cht-text-xs cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest cht-pb-4">Ticket</th>
                      <th className="cht-text-xs cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest cht-pb-4">Category</th>
                      <th className="cht-text-xs cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest cht-pb-4">Subject</th>
                      <th className="cht-text-xs cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest cht-pb-4">Last Updated</th>
                      <th className="cht-text-xs cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest cht-pb-4">Status</th>
                      <th className="cht-text-xs cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest cht-pb-4 cht-text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTickets.map((ticket) => (
                      <tr
                        key={ticket.id}
                        className="cht-border-b cht-border-gray-100 hover:cht-bg-gray-50/50 cht-transition-all"
                        style={{ height: 56 }}
                      >
                        <td className="cht-text-xs cht-font-black cht-text-gray-500">
                          {ticket.ticket_number}
                        </td>
                        <td className="cht-text-xs cht-font-bold cht-text-black">
                          {ticket.topic}
                        </td>
                        <td className="cht-text-sm cht-font-bold cht-text-black max-w-xs cht-truncate">
                          {ticket.subject}
                        </td>
                        <td className="cht-text-xs cht-text-gray-400 cht-font-bold">
                          {new Date(ticket.last_updated).toLocaleDateString()}
                        </td>
                        <td>
                          <TicketStatusBadge status={ticket.status} />
                        </td>
                        <td className="cht-text-right">
                          <Button
                            onClick={() => setSelectedTicket(ticket)}
                            variant="ghost"
                            className="cht-text-[#3e57da] hover:cht-bg-[#f2f4fd] !cht-py-2 !cht-px-4 cht-rounded-xl"
                            size="sm"
                          >
                            View Thread
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

      </main>

      {/* Slide-over Conversation Drawer */}
      <AnimatePresence>
        {selectedTicket && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.4 }}
              exit={{ opacity: 0 }}
              onClick={() => setSelectedTicket(null)}
              className="cht-fixed cht-inset-0 cht-bg-black/40 cht-backdrop-blur-sm cht-z-[1000]"
            />

            {/* Panel */}
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              className="cht-fixed cht-right-0 cht-top-0 cht-h-full cht-w-full md:cht-w-[500px] cht-bg-white cht-shadow-2xl cht-z-[1001] cht-flex cht-flex-col"
            >
              {/* Drawer Header */}
              <div className="cht-p-6 cht-border-b cht-border-gray-100 cht-flex cht-items-center cht-justify-between">
                <div className="cht-space-y-1">
                  <div className="cht-flex cht-items-center cht-gap-2">
                    <span className="cht-text-xs cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">
                      {selectedTicket.ticket_number}
                    </span>
                    <TicketStatusBadge status={selectedTicket.status} />
                  </div>
                  <h4 className="cht-text-lg cht-font-black cht-text-black cht-line-clamp-1">
                    {selectedTicket.subject}
                  </h4>
                </div>
                <button
                  onClick={() => setSelectedTicket(null)}
                  className="cht-p-2 hover:cht-bg-gray-100 cht-rounded-full cht-text-gray-400 hover:cht-text-black cht-transition-colors"
                >
                  <X size={20} />
                </button>
              </div>

              {/* Drawer Messages Scroll Area */}
              <div className="cht-flex-1 cht-overflow-y-auto cht-p-6 cht-space-y-6 cht-bg-gray-50/50">
                <div className="cht-p-4.5 cht-bg-white cht-border cht-border-gray-100/80 cht-rounded-[18px] cht-shadow-sm">
                  <span className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">
                    Original Request
                  </span>
                  <p className="cht-text-sm cht-text-gray-600 cht-mt-2 cht-font-medium leading-relaxed">
                    {selectedTicket.description}
                  </p>
                </div>

                <div className="cht-space-y-4">
                  {selectedTicket.messages.map((msg, idx) => {
                    // Skips the first message as it's the description card above
                    if (idx === 0) return null;
                    const isStudent = msg.sender === "student";
                    return (
                      <div
                        key={msg.id}
                        className={`cht-flex cht-flex-col ${
                          isStudent ? "cht-items-end" : "cht-items-start"
                        }`}
                      >
                        <div className="cht-flex cht-items-center cht-gap-2 cht-mb-1">
                          <span className="cht-text-[10px] cht-font-bold cht-text-gray-400">
                            {msg.sender_name}
                          </span>
                          <span className="cht-text-[9px] cht-text-gray-300">{formatMessageTime(msg.timestamp)}</span>
                        </div>
                        <div
                          className={`cht-max-w-[85%] cht-p-4 cht-rounded-[18px] cht-text-sm cht-font-medium leading-relaxed ${
                            isStudent
                              ? "cht-bg-[#3e57da] cht-text-white cht-rounded-tr-none"
                              : "cht-bg-white cht-border cht-border-gray-100 cht-text-black cht-rounded-tl-none"
                          }`}
                        >
                          {msg.content}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Drawer Input / Actions */}
              <div className="cht-p-6 cht-border-t cht-border-gray-100 cht-space-y-4 cht-bg-white">
                <div className="cht-flex cht-items-center cht-justify-between">
                  <span className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">
                    Post Reply
                  </span>
                </div>
                
                <div className="cht-flex cht-gap-3">
                  <input
                    type="text"
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    placeholder={selectedTicket.status === "Resolved" ? "This ticket has been resolved." : "Type your follow-up message..."}
                    disabled={selectedTicket.status === "Resolved"}
                    className="cht-flex-1 cht-bg-gray-50 cht-border-none cht-rounded-[16px] cht-px-4.5 cht-py-3.5 cht-text-sm cht-font-semibold cht-outline-none focus:cht-ring-2 focus:cht-ring-[#3e57da]/10 transition-all disabled:cht-opacity-50"
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && replyText.trim()) handleSendReply(selectedTicket.id);
                    }}
                  />
                  <Button
                    onClick={() => handleSendReply(selectedTicket.id)}
                    disabled={!replyText.trim() || selectedTicket.status === "Resolved"}
                    variant="primary"
                    className="cht-rounded-[16px] !cht-py-3.5 !cht-px-5"
                  >
                    <Send size={16} />
                  </Button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </section>
  );
};

export default SupportPage;
